// 1080 x 1350 share card generator.
// Draws dark gradient bg + subtle noise + double gold border + typography.

const W = 1080;
const H = 1350;
const GOLD = "#c9a755";
const GOLD_LIGHT = "#e8cf8f";
const TEXT = "#e9e6dd";
const MUTED = "#9a97a4";

function roundedRect(ctx, x, y, w, h, r) {
  ctx.beginPath();
  ctx.moveTo(x + r, y);
  ctx.arcTo(x + w, y, x + w, y + h, r);
  ctx.arcTo(x + w, y + h, x, y + h, r);
  ctx.arcTo(x, y + h, x, y, r);
  ctx.arcTo(x, y, x + w, y, r);
  ctx.closePath();
}

function drawNoise(ctx, w, h, alpha = 12) {
  const img = ctx.getImageData(0, 0, w, h);
  const d = img.data;
  for (let i = 0; i < d.length; i += 4) {
    const n = (Math.random() * 2 - 1) * alpha;
    d[i] = Math.max(0, Math.min(255, d[i] + n));
    d[i + 1] = Math.max(0, Math.min(255, d[i + 1] + n));
    d[i + 2] = Math.max(0, Math.min(255, d[i + 2] + n));
  }
  ctx.putImageData(img, 0, 0);
}

// Draws the share card and returns the canvas element.
export function drawShareCard({ philosopher, pct, topTraits }) {
  const canvas = document.createElement("canvas");
  canvas.width = W;
  canvas.height = H;
  const ctx = canvas.getContext("2d");

  // Background gradient (dark radial + linear).
  const bgLin = ctx.createLinearGradient(0, 0, 0, H);
  bgLin.addColorStop(0, "#12131a");
  bgLin.addColorStop(1, "#07080c");
  ctx.fillStyle = bgLin;
  ctx.fillRect(0, 0, W, H);

  const glow = ctx.createRadialGradient(W / 2, 260, 40, W / 2, 260, 700);
  glow.addColorStop(0, "rgba(201,167,85,0.28)");
  glow.addColorStop(0.5, "rgba(201,167,85,0.08)");
  glow.addColorStop(1, "rgba(201,167,85,0)");
  ctx.fillStyle = glow;
  ctx.fillRect(0, 0, W, H);

  // Add subtle noise.
  drawNoise(ctx, W, H, 6);

  // Double gold border frame.
  ctx.strokeStyle = "rgba(201,167,85,0.65)";
  ctx.lineWidth = 3;
  roundedRect(ctx, 40, 40, W - 80, H - 80, 14);
  ctx.stroke();

  ctx.strokeStyle = "rgba(201,167,85,0.35)";
  ctx.lineWidth = 1.5;
  roundedRect(ctx, 60, 60, W - 120, H - 120, 8);
  ctx.stroke();

  ctx.textAlign = "center";
  ctx.textBaseline = "alphabetic";

  // Ornament ❦
  ctx.fillStyle = GOLD;
  ctx.font = '500 64px "Playfair Display", Georgia, serif';
  ctx.fillText("❦", W / 2, 200);

  // Eyebrow "I AM"
  ctx.fillStyle = MUTED;
  ctx.font = '600 28px "Inter", sans-serif';
  ctx.fillText("I  A M", W / 2, 265);

  // Philosopher name (large gold caps, wrapping if needed)
  ctx.fillStyle = GOLD;
  const name = philosopher.name.toUpperCase();
  ctx.font = '700 82px "Playfair Display", Georgia, serif';
  const nameWidth = ctx.measureText(name).width;
  if (nameWidth > W - 200) {
    const parts = name.split(" ");
    const mid = Math.ceil(parts.length / 2);
    const l1 = parts.slice(0, mid).join(" ");
    const l2 = parts.slice(mid).join(" ");
    ctx.fillText(l1, W / 2, 370);
    ctx.fillText(l2, W / 2, 450);
  } else {
    ctx.fillText(name, W / 2, 400);
  }

  // Title (italic gold-light)
  ctx.fillStyle = GOLD_LIGHT;
  ctx.font = 'italic 400 36px "Playfair Display", Georgia, serif';
  ctx.fillText(philosopher.title, W / 2, 510);

  // Huge percentage
  ctx.fillStyle = TEXT;
  ctx.font = '700 200px "Playfair Display", Georgia, serif';
  ctx.fillText(`${pct}%`, W / 2, 730);

  ctx.fillStyle = MUTED;
  ctx.font = '600 30px "Inter", sans-serif';
  ctx.fillText("M A T C H", W / 2, 815);

  // Divider
  ctx.strokeStyle = "rgba(201,167,85,0.4)";
  ctx.lineWidth = 1;
  ctx.beginPath();
  ctx.moveTo(W / 2 - 120, 850);
  ctx.lineTo(W / 2 + 120, 850);
  ctx.stroke();

  // Top 4 traits with letter grades — never weaknesses.
  const startY = 900;
  const rowH = 62;
  ctx.textAlign = "left";
  const gradeX = W - 300; // Fixed left-anchor for grade letter — keeps A, A+, B, B+ aligned.
  topTraits.slice(0, 4).forEach((t, i) => {
    const y = startY + i * rowH;
    ctx.fillStyle = TEXT;
    ctx.font = '500 34px "Playfair Display", Georgia, serif';
    ctx.textAlign = "left";
    ctx.fillText(t.label.toUpperCase(), 180, y);

    // Dotted line between label and grade
    ctx.strokeStyle = "rgba(154,151,164,0.35)";
    ctx.setLineDash([2, 6]);
    ctx.beginPath();
    const labelW = ctx.measureText(t.label.toUpperCase()).width;
    ctx.moveTo(180 + labelW + 20, y - 10);
    ctx.lineTo(gradeX - 20, y - 10);
    ctx.stroke();
    ctx.setLineDash([]);

    ctx.fillStyle = GOLD_LIGHT;
    ctx.font = '600 40px "Playfair Display", Georgia, serif';
    ctx.textAlign = "left";
    ctx.fillText(t.grade, gradeX, y);
  });

  // Bottom section
  ctx.textAlign = "center";
  ctx.fillStyle = TEXT;
  ctx.font = 'italic 400 32px "Playfair Display", Georgia, serif';
  ctx.fillText("Which philosopher are you?", W / 2, H - 160);

  ctx.fillStyle = GOLD;
  ctx.font = '500 26px "Inter", sans-serif';
  ctx.fillText("thephilosophertest.com", W / 2, H - 110);

  return canvas;
}

// Turns canvas -> Blob (PNG).
export function canvasToBlob(canvas) {
  return new Promise((resolve) => {
    canvas.toBlob((b) => resolve(b), "image/png", 0.95);
  });
}

// Web Share API with file if supported, otherwise download.
export async function shareOrDownload(blob, filename, textMeta) {
  const file = new File([blob], filename, { type: "image/png" });
  const nav = typeof navigator !== "undefined" ? navigator : null;
  if (
    nav &&
    typeof nav.canShare === "function" &&
    nav.canShare({ files: [file] }) &&
    typeof nav.share === "function"
  ) {
    try {
      await nav.share({
        files: [file],
        title: "The Philosopher Test",
        text: textMeta,
      });
      return { shared: true };
    } catch (err) {
      // Web Share cancelled or blocked — fall through to download.
      console.debug("[shareCard] Web Share unavailable, falling back:", err?.name || err);
    }
  }
  // Fallback: download.
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  setTimeout(() => URL.revokeObjectURL(url), 1000);
  return { shared: false, downloaded: true };
}
